import { SupportSurvey, supportSurveyApi } from '@/api/queries/supportSurvey'
import { CalendarOutlined, FormOutlined, InfoCircleOutlined, TeamOutlined, VideoCameraOutlined, WalletOutlined } from '@ant-design/icons'
import Button from '@UI/New/Button'
import Textarea from '@UI/Textarea'
import { Modal, Rate, Tooltip } from 'antd'
import { CSSProperties, FC, useCallback, useEffect, useRef, useState } from 'react'
import classes from './index.module.css'

interface Props {
  profile?: { id?: number | string; role?: string; roles?: string[] }
}

const formatDate = (timestamp: number) =>
  new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Moscow',
  }).format(new Date(timestamp * 1000))

const metrics: [keyof SupportSurvey['stats'], string, string][] = [
  ['users', 'Активных пользователей', 'Сколько разных пользователей заходили на платформу за неделю. Каждый учитывается один раз, сколько бы раз он ни заходил.'],
  ['lessons', 'Проведённых уроков', 'Занятия из расписания, которые отмечены как проведённые в течение недели.'],
  ['homeworks', 'Домашних заданий', 'Все выполненные за неделю работы, независимо от типа ДЗ. Каждая работа учитывается целиком.'],
  ['paymentsAmount', 'Сумма оплат за неделю', 'Деньги, полученные от учеников за неделю: онлайн и ручные оплаты, включая пополнения баланса. Учитывается дата платежа, независимо от оплаченного периода. Списания с внутреннего баланса и возвраты не включаются.'],
]

const ratingLabels = ['😞 Очень плохо', '😕 Плохо', '😐 Нормально', '🙂 Хорошо', '😍 Отлично']

const InfoIcon = InfoCircleOutlined as unknown as FC
const CalendarIcon = CalendarOutlined as unknown as FC

const metricIcons = {
  users: TeamOutlined,
  lessons: VideoCameraOutlined,
  homeworks: FormOutlined,
  paymentsAmount: WalletOutlined,
} as unknown as Record<keyof SupportSurvey['stats'], FC>

const SupportSurveyModal: FC<Props> = ({ profile }) => {
  const [survey, setSurvey] = useState<SupportSurvey | null>(null)
  const [rating, setRating] = useState(0)
  const [hoverRating, setHoverRating] = useState(0)
  const [comment, setComment] = useState('')
  const [commentFocused, setCommentFocused] = useState(false)
  const [showThanks, setShowThanks] = useState(false)
  const [thanksHeight, setThanksHeight] = useState<number>()
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const [refresh, setRefresh] = useState(0)
  const surveyRef = useRef<SupportSurvey | null>(null)
  const generation = useRef(0)
  const submitting = useRef(false)
  const bodyRef = useRef<HTMLDivElement>(null)
  const isAdmin = Boolean(
    ['admin', 'junior-admin'].includes(profile?.role ?? '') ||
    profile?.roles?.some?.((role) => ['admin', 'junior-admin'].includes(role)),
  )

  useEffect(() => {
    const current = ++generation.current
    surveyRef.current = null
    submitting.current = false
    setSurvey(null)
    setSending(false)
    setError('')
    setShowThanks(false)
    if (!isAdmin || !profile?.id) return
    let disposed = false
    let loading = false
    let lastCheck = 0
    const check = async () => {
      if (disposed || loading || surveyRef.current || document.hidden || Date.now() - lastCheck < 10000) return
      loading = true
      lastCheck = Date.now()
      try {
        const response = await supportSurveyApi.pending()
        if (!disposed && current === generation.current && response.data.status === 'success' && response.data.data) {
          surveyRef.current = response.data.data
          setSurvey(response.data.data)
          setRating(0)
          setHoverRating(0)
          setComment('')
          setCommentFocused(false)
          setError('')
        }
      } catch {
        // Retry on the next visible poll. A network failure must not block the platform.
      } finally {
        loading = false
      }
    }
    void check()
    const interval = window.setInterval(check, 60000)
    window.addEventListener('focus', check)
    document.addEventListener('visibilitychange', check)
    return () => {
      disposed = true
      ++generation.current
      window.clearInterval(interval)
      window.removeEventListener('focus', check)
      document.removeEventListener('visibilitychange', check)
    }
  }, [isAdmin, profile?.id, refresh])

  const finish = useCallback(() => {
    surveyRef.current = null
    setShowThanks(false)
    setSurvey(null)
    setRefresh((value) => value + 1)
  }, [])

  useEffect(() => {
    if (!showThanks) return
    let resizeFrame = 0
    const frame = window.requestAnimationFrame(() => {
      resizeFrame = window.requestAnimationFrame(() => setThanksHeight(Math.min(420, Math.max(340, window.innerHeight - 160))))
    })
    const timeout = window.setTimeout(finish, 3200)
    return () => {
      window.cancelAnimationFrame(frame)
      window.cancelAnimationFrame(resizeFrame)
      window.clearTimeout(timeout)
    }
  }, [showThanks, finish])

  const submit = async () => {
    if (!survey || rating < 1 || submitting.current || showThanks) return
    const current = generation.current
    submitting.current = true
    setSending(true)
    setError('')
    try {
      const response = await supportSurveyApi.submit(survey.id, rating, comment.trim())
      if (response.data.status !== 'success' || !response.data.data.submitted) throw new Error()
      if (generation.current === current) {
        setThanksHeight(bodyRef.current?.getBoundingClientRect().height || 420)
        setShowThanks(true)
      }
    } catch {
      if (generation.current === current) {
        setError('Не удалось отправить отзыв. Проверьте соединение и нажмите «Отправить» ещё раз.')
      }
    } finally {
      if (generation.current === current) {
        submitting.current = false
        setSending(false)
      }
    }
  }

  return (
    <Modal
      open={Boolean(isAdmin && survey)}
      closable={false}
      maskClosable={showThanks}
      keyboard={showThanks}
      onCancel={showThanks ? finish : undefined}
      footer={null}
      width={700}
      centered
      zIndex={1200}
      className={classes.modal}
      title={showThanks ? <span className={classes.modalTitle}>Отзыв отправлен</span> : (
        <span className={classes.titleWrap}>
          <span className={classes.eyebrow} aria-hidden="true">✦ Итоги недели</span>
          <span className={classes.modalTitle}>Ваша неделя с Импульс ЛМС</span>
        </span>
      )}
      destroyOnClose
    >
      {survey && (
        <div ref={bodyRef} className={`${classes.surveyBody} ${showThanks ? classes.answered : ''}`} style={showThanks ? { height: thanksHeight } : undefined}>
        <form className={classes.content} aria-hidden={showThanks || undefined} onSubmit={(event) => { event.preventDefault(); void submit() }}>
          <section className={classes.week} aria-labelledby="support-survey-week">
            <div className={classes.weekHeader}>
              <h2 id="support-survey-week">Как прошла неделя</h2>
              <span className={classes.period}>
                <span className={classes.periodIcon} aria-hidden="true"><CalendarIcon /></span>
                {formatDate(survey.periodFrom)} — {formatDate(survey.periodUntil)} МСК
              </span>
            </div>
            <div className={classes.metrics}>
              {metrics.map(([key, label, hint]) => {
                const Icon = metricIcons[key]
                return (
                  <article key={key} className={classes.metric} data-metric={key}>
                    <div className={classes.metricTop}>
                      <span className={classes.metricIcon} aria-hidden="true"><Icon /></span>
                      <Tooltip title={hint} placement="top" trigger={['hover', 'focus']} color="#2a2338" zIndex={1300} overlayClassName={classes.tooltip}>
                        <button type="button" className={classes.metricInfo} aria-label={`${label}: ${hint}`}><InfoIcon /></button>
                      </Tooltip>
                    </div>
                    <strong className={classes.metricValue}>{survey.stats[key]?.toLocaleString('ru-RU', key === 'paymentsAmount'
                      ? { style: 'currency', currency: 'RUB', minimumFractionDigits: 0, maximumFractionDigits: 2 }
                      : undefined) ?? '—'}</strong>
                    <span className={classes.metricLabel}>{label}</span>
                  </article>
                )
              })}
            </div>
            {metrics.some(([key]) => survey.stats[key] == null) && <p className={classes.statsNote}>Часть статистики пока недоступна.</p>}
          </section>
          <div className={classes.rating}>
            <h2 id="support-survey-rating">Как вам работа с платформой?</h2>
            <p>Ваша оценка помогает нам становиться лучше</p>
            <Rate value={rating} onChange={setRating} onHoverChange={(value) => setHoverRating(value || 0)} allowClear={false} disabled={sending} aria-labelledby="support-survey-rating" />
            <span key={hoverRating || rating} className={classes.ratingCaption} aria-hidden="true">{ratingLabels[(hoverRating || rating) - 1] || 'Выберите оценку'}</span>
          </div>
          <label className={classes.label} htmlFor="support-survey-comment">Расскажите, что вам нравится и чего не хватает</label>
          <Textarea
            error={false}
            style={{ width: '100%' }}
            inputRef={undefined}
            id="support-survey-comment"
            className={`${classes.comment} ${commentFocused || comment.trim() ? classes.commentExpanded : ''}`}
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            onFocus={() => setCommentFocused(true)}
            onBlur={() => setCommentFocused(false)}
            maxLength={5000}
            rows={1}
            maxAutoHeight={180}
            disabled={sending}
            placeholder="Написать отзыв…"
          />
          <div className={classes.footer}>
            <span>Комментарий необязателен.<br />Можно отправить только оценку.</span>
            <Button type="submit" isLoading={sending} disabled={sending || rating < 1} className={classes.submitButton}>Отправить</Button>
          </div>
          {error && <p className={classes.error} role="alert">{error}</p>}
        </form>
        {showThanks && (
          <section className={classes.thanks} aria-label="Отзыв отправлен">
            <div className={classes.celebration} aria-hidden="true">
              <span className={classes.successHalo} />
              {Array.from({ length: 8 }, (_, index) => (
                <i key={index} className={classes.particle} style={{ '--angle': `${index * 45}deg`, '--delay': `${index * 30}ms` } as CSSProperties} />
              ))}
              <span className={classes.successIcon}>
                <svg viewBox="0 0 48 48" fill="none"><path d="m12 24 8 8 16-17" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </span>
              <span className={classes.successSparkle}>✦</span>
              <span className={classes.successSparkleSmall}>✦</span>
            </div>
            <div className={classes.thanksCopy} role="status">
              <h2>Спасибо за ваш ответ!</h2>
              <p>Вы помогаете нам делать<br />Импульс ЛМС лучше.</p>
            </div>
            <Button type="button" autoFocus onClick={finish} className={classes.continueButton}>Продолжить</Button>
          </section>
        )}
        </div>
      )}
    </Modal>
  )
}

export default SupportSurveyModal
